import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Box, Card, CardActionArea, CardContent, Chip, Grid, LinearProgress, Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup,
    Tooltip, Typography, alpha, useMediaQuery, useTheme } from "@mui/material";
import { DataGrid, deDE } from "@mui/x-data-grid";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Header from "../../components/header";
import { UserContext } from "../../theme/userContext";
import Barometer from "./barometer";
import { fetchJson } from "./opportunityApi";
import OpportunityDetail from "./opportunityDetail";
import { DASH, DOSSIER_STATE_LABELS, FALLBACK_DISCLAIMER, FAZIT_HINT, FAZIT_LABELS, fmtCount, fmtDate, fmtLarge, fmtMultiple, fmtPct, fmtPctSigned, fmtPrice,
    germanizeText, hitFlags, matchesRatingFilter, RATING_LABELS, toNumber, YIELD_MEASURES } from "./opportunityFormat";

const GRID_LOCALE = deDE.components.MuiDataGrid.defaultProps.localeText;

const RATING_FILTERS = [
    { value: 'open', label: 'Ohne verworfene' },
    { value: 'unrated', label: 'Nicht bewertet' },
    { value: 'interesting', label: RATING_LABELS.interesting },
    { value: 'bought', label: RATING_LABELS.bought },
    { value: 'rejected', label: RATING_LABELS.rejected },
    { value: 'all', label: 'Alle' },
];

const REVIEW_FILTERS = [
    { value: 'all', label: 'Alle' },
    { value: 'interesting', label: RATING_LABELS.interesting },
    { value: 'bought', label: RATING_LABELS.bought },
    { value: 'rejected', label: RATING_LABELS.rejected },
];

function NameCell({ name, identifier }) {
    return (
        <Box sx={{ minWidth: 0, lineHeight: 1.2 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 'bold' }} title={name || identifier}>{name || identifier}</Typography>
            <Typography variant="caption" noWrap sx={{ opacity: 0.75, display: 'block' }}>{identifier}</Typography>
        </Box>
    );
}

function YieldCell({ row }) {
    const measure = YIELD_MEASURES[row.yield_measure];
    const flags = hitFlags(row);
    return (
        <Box sx={{ lineHeight: 1.2, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>{fmtPct(row.yield_value)}</Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
                {flags.length > 0 ? (
                    <Tooltip title={flags.map((flag) => flag.text).join(' ')} arrow>
                        <Stack direction="row" spacing={0.25} alignItems="center">
                            <WarningAmberIcon sx={{ fontSize: 13 }} color="warning" />
                            <Typography variant="caption" noWrap>{flags[0].label}{flags.length > 1 ? ` +${flags.length - 1}` : ''}</Typography>
                        </Stack>
                    </Tooltip>
                ) : (
                    <Tooltip title={measure?.long || ''} arrow>
                        <Typography variant="caption" noWrap sx={{ opacity: 0.75 }}>{measure?.short || DASH}</Typography>
                    </Tooltip>
                )}
            </Stack>
        </Box>
    );
}

function RatingChip({ rating }) {
    if (!rating) return <Typography variant="body2" sx={{ opacity: 0.6 }}>{DASH}</Typography>;
    const color = rating === 'rejected' ? 'default' : 'secondary';
    return <Chip size="small" color={color} variant={rating === 'bought' ? 'filled' : 'outlined'} label={RATING_LABELS[rating] || DASH} />;
}

function FazitChip({ row }) {
    const fazit = FAZIT_LABELS[row.dossier_fazit];
    if (!fazit) return <Typography variant="body2" sx={{ opacity: 0.6 }}>{DASH}</Typography>;
    const title = [germanizeText(row.dossier_fazit_text || ''), FAZIT_HINT].filter(Boolean).join(' ');
    const highlighted = row.dossier_fazit === 'genauer_pruefen';
    return (
        <Tooltip title={title} arrow>
            <Chip
                size="small"
                color={highlighted ? 'success' : 'default'}
                variant={highlighted ? 'filled' : 'outlined'}
                icon={highlighted ? <SearchIcon /> : undefined}
                label={fazit.label}
                sx={highlighted ? { fontWeight: 'bold' } : { opacity: row.dossier_fazit === 'eher_aussortieren' ? 0.7 : 1 }}
            />
        </Tooltip>
    );
}

function hitColumns() {
    return [
        {
            field: 'name', headerName: 'Name', minWidth: 160, flex: 1.4,
            renderCell: ({ row }) => <NameCell name={row.name} identifier={row.identifier} />,
        },
        {
            field: 'dossier_fazit', headerName: 'Fazit', width: 150,
            valueGetter: ({ row }) => FAZIT_LABELS[row.dossier_fazit]?.rank ?? 0,
            renderCell: ({ row }) => <FazitChip row={row} />,
        },
        { field: 'sector', headerName: 'Sektor', minWidth: 100, flex: 0.8, valueFormatter: ({ value }) => value || DASH },
        {
            field: 'yield_value', headerName: 'Rendite', width: 120, type: 'number', align: 'left', headerAlign: 'left',
            valueGetter: ({ row }) => toNumber(row.yield_value),
            renderCell: ({ row }) => <YieldCell row={row} />,
        },
        {
            field: 'own_median', headerName: 'Eigener Median', width: 85, type: 'number',
            valueGetter: ({ row }) => toNumber(row.own_median),
            valueFormatter: ({ value }) => fmtPct(value),
        },
        {
            field: 'distance_to_history', headerName: 'Abstand', width: 90, type: 'number',
            valueGetter: ({ row }) => toNumber(row.distance_to_history),
            valueFormatter: ({ value }) => fmtMultiple(value),
        },
        {
            field: 'price_change_12m', headerName: 'Kurs 12 M', width: 85, type: 'number',
            valueGetter: ({ row }) => toNumber(row.price_change_12m),
            valueFormatter: ({ value }) => fmtPctSigned(value),
        },
        {
            field: 'revenue_yoy', headerName: 'Umsatz ggü. Vorjahr', width: 85, type: 'number',
            valueGetter: ({ row }) => toNumber(row.revenue_yoy),
            valueFormatter: ({ value }) => fmtPctSigned(value),
        },
        {
            field: 'market_cap', headerName: 'Börsenwert', width: 105, type: 'number',
            valueGetter: ({ row }) => toNumber(row.market_cap),
            renderCell: ({ row }) => fmtLarge(row.market_cap, row.price_currency),
        },
        {
            field: 'first_as_of', headerName: 'Auf der Liste seit', width: 105,
            renderCell: ({ row }) => (
                <Box sx={{ lineHeight: 1.2 }}>
                    <Typography variant="body2">{fmtDate(row.first_as_of)}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>{fmtCount(row.runs_on_list)} Läufe</Typography>
                </Box>
            ),
        },
        {
            field: 'dossier_state', headerName: 'Dossier', width: 118,
            valueFormatter: ({ value }) => DOSSIER_STATE_LABELS[value] || DASH,
        },
        {
            field: 'my_rating', headerName: 'Eigene Bewertung', width: 105,
            valueGetter: ({ row }) => row.my_rating?.rating || '',
            renderCell: ({ value }) => <RatingChip rating={value} />,
        },
    ];
}

function reviewColumns() {
    return [
        {
            field: 'name', headerName: 'Name', minWidth: 170, flex: 1.2,
            renderCell: ({ row }) => <NameCell name={row.name} identifier={row.identifier} />,
        },
        { field: 'rating', headerName: 'Bewertung', width: 115, renderCell: ({ value }) => <RatingChip rating={value} /> },
        { field: 'rated_at', headerName: 'Markiert am', width: 105, valueFormatter: ({ value }) => fmtDate(value) },
        {
            field: 'price_at_mark', headerName: 'Kurs beim Markieren', width: 125, type: 'number',
            valueGetter: ({ row }) => toNumber(row.price_at_mark),
            valueFormatter: ({ value }) => fmtPrice(value),
        },
        {
            field: 'last_change', headerName: 'Seit Markieren', width: 105, type: 'number',
            valueGetter: ({ row }) => toNumber(row.track?.last_change),
            valueFormatter: ({ value }) => fmtPctSigned(value),
        },
        {
            field: 'last_date', headerName: 'Letzter Kurs', width: 105,
            valueGetter: ({ row }) => row.track?.last_date || '',
            valueFormatter: ({ value }) => fmtDate(value),
        },
        {
            field: 'note', headerName: 'Notiz', minWidth: 160, flex: 1, sortable: false,
            renderCell: ({ value }) => (value ? <Typography variant="body2" noWrap title={value}>{value}</Typography> : DASH),
        },
    ];
}

function FilterBar({ options, value, onChange, label }) {
    return (
        <ToggleButtonGroup
            exclusive
            size="small"
            color="secondary"
            value={value}
            onChange={(event, next) => { if (next) onChange(next); }}
            aria-label={label}
            sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { textTransform: 'none', py: 0.25 } }}
        >
            {options.map((option) => (
                <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>
            ))}
        </ToggleButtonGroup>
    );
}

function DataCheckSection({ rows, onOpen }) {
    const theme = useTheme();
    return (
        <Box sx={{ mt: 4, p: 2, borderRadius: 1, border: `1px dashed ${theme.palette.warning.main}` }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <WarningAmberIcon color="warning" />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Daten prüfen ({fmtCount(rows.length)})</Typography>
            </Stack>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
                Diese Papiere zählen nicht als Treffer. Ihre Daten sehen unplausibel aus und sollten erst geprüft werden.
            </Typography>
            {rows.length === 0 && <Typography variant="body2" sx={{ opacity: 0.75 }}>In diesem Lauf gibt es keine auffälligen Daten.</Typography>}
            <Grid container spacing={1}>
                {rows.map((row) => (
                    <Grid item xs={12} md={6} key={row.identifier}>
                        <Card variant="outlined" sx={{ height: '100%', backgroundColor: 'transparent' }}>
                            <CardActionArea onClick={() => onOpen(row.identifier)} sx={{ height: '100%' }}>
                                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {row.name || row.identifier} <Typography component="span" variant="caption" sx={{ opacity: 0.75 }}>{row.identifier}</Typography>
                                    </Typography>
                                    {(Array.isArray(row.data_check_reasons) ? row.data_check_reasons : []).map((reason, index) => (
                                        <Typography variant="caption" sx={{ display: 'block' }} key={reason.code || index}>{germanizeText(reason.text)}</Typography>
                                    ))}
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

const Opportunities = () => {
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
    const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = useContext(UserContext);
    const pUser = user?.p_user;

    const [tab, setTab] = useState(0);
    const [list, setList] = useState(null);
    const [listError, setListError] = useState(null);
    const [points, setPoints] = useState([]);
    const [barometerError, setBarometerError] = useState(null);
    const [ratings, setRatings] = useState([]);
    const [ratingsError, setRatingsError] = useState(null);
    const [ratingFilter, setRatingFilter] = useState('open');
    const [reviewFilter, setReviewFilter] = useState('all');
    const [selected, setSelected] = useState(null);

    const loadList = useCallback(async () => {
        if (!pUser) return;
        try {
            setList(await fetchJson(`/api/opportunities?puser=${encodeURIComponent(pUser)}`));
            setListError(null);
        } catch (error) {
            setListError(error.message);
        }
    }, [pUser]);

    const loadRatings = useCallback(async () => {
        if (!pUser) return;
        try {
            const data = await fetchJson(`/api/opportunities/ratings?puser=${encodeURIComponent(pUser)}`);
            setRatings(Array.isArray(data?.ratings) ? data.ratings : []);
            setRatingsError(null);
        } catch (error) {
            setRatingsError(error.message);
        }
    }, [pUser]);

    useEffect(() => {
        loadList();
        loadRatings();
        fetchJson('/api/opportunities/barometer')
            .then((data) => { setPoints(Array.isArray(data?.points) ? data.points : []); setBarometerError(null); })
            .catch((error) => setBarometerError(error.message));
    }, [loadList, loadRatings]);

    const refreshAfterRating = useCallback(async () => {
        await Promise.all([loadList(), loadRatings()]);
    }, [loadList, loadRatings]);

    const run = list?.run || null;
    const disclaimer = list?.disclaimer || FALLBACK_DISCLAIMER;
    const warnings = Array.isArray(run?.warnings) ? run.warnings.filter((warning) => typeof warning === 'string') : [];
    const hits = useMemo(() => (Array.isArray(list?.hits) ? list.hits : []), [list]);
    const dataCheck = Array.isArray(list?.data_check) ? list.data_check : [];
    const visibleHits = useMemo(() => hits.filter((hit) => matchesRatingFilter(hit, ratingFilter)), [hits, ratingFilter]);
    const hiddenRejected = ratingFilter === 'open' ? hits.length - visibleHits.length : 0;
    const visibleRatings = reviewFilter === 'all' ? ratings : ratings.filter((rating) => rating.rating === reviewFilter);
    const latestQuote = run?.price_info?.latest_quote_date;

    const columns = useMemo(hitColumns, []);
    const ratingColumnsDef = useMemo(reviewColumns, []);
    const hitVisibility = useMemo(() => ({
        sector: !isNarrow,
        own_median: !isPhone,
        price_change_12m: !isPhone,
        revenue_yoy: !isNarrow,
        market_cap: !isNarrow,
        first_as_of: !isNarrow,
        dossier_state: !isPhone,
    }), [isNarrow, isPhone]);
    const reviewVisibility = useMemo(() => ({
        price_at_mark: !isPhone,
        last_date: !isPhone,
        rated_at: !isPhone,
        note: !isPhone,
    }), [isPhone]);

    const gridSx = {
        cursor: 'pointer',
        '& .fazit-genauer-pruefen': {
            backgroundColor: alpha(theme.palette.success.main, 0.1),
            '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.18) },
        },
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
        '& .MuiDataGrid-columnHeaderTitle': { whiteSpace: 'normal', lineHeight: 1.2 },
    };

    return (
        <Box sx={{ m: { xs: '12px', sm: '20px' }, minWidth: 0 }}>
            <Header title="GELEGENHEITEN" subtitle={run ? `Stichtag ${fmtDate(run.as_of)}` : 'Wöchentliche Arbeitsliste'} />

            <Alert severity="info" sx={{ mb: 2 }}>{disclaimer}</Alert>

            {warnings.map((warning, index) => (
                <Alert severity="warning" sx={{ mb: 1 }} key={index}>{germanizeText(warning)}</Alert>
            ))}
            {toNumber(run?.paper_errors) > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>Bei {fmtCount(run.paper_errors)} Papieren gab es im Lauf einen Fehler. Sie fehlen in der Auswertung.</Alert>
            )}

            {listError && <Alert severity="error" sx={{ mb: 2 }}>Die Liste konnte nicht geladen werden: {listError}</Alert>}
            {!list && !listError && <LinearProgress color="secondary" sx={{ mb: 2 }} />}

            {list && !run && (
                <Alert severity="info" sx={{ mb: 2 }}>Es gibt noch keinen Lauf. Die Liste entsteht im wöchentlichen Lauf.</Alert>
            )}

            {run && (
                <>
                    <Typography variant="body2" sx={{ opacity: 0.75, mb: 1.5 }}>
                        Lauf vom {fmtDate(run.finished_at || run.started_at)}
                        {latestQuote ? `, Kursstand ${fmtDate(latestQuote)}` : ''}.
                        {' '}Dossiers: {fmtCount(run.dossier_created)} neu, {fmtCount(run.dossier_reused)} wiederverwendet, {fmtCount(run.dossier_no_sources)} ohne Quellen,
                        {' '}{fmtCount(run.dossier_failed)} fehlgeschlagen, {fmtCount(run.dossier_skipped_budget)} nicht erstellt.
                        {toNumber(run.dossier_eod_news_calls) > 0 && ` EOD-Nachrichten: ${fmtCount(run.dossier_eod_news_calls)} Abrufe, ${fmtCount(run.dossier_eod_credits)} Credits.`}
                    </Typography>

                    <Barometer current={list.barometer} points={points} error={barometerError} />

                    <Tabs value={tab} onChange={(event, value) => setTab(value)} textColor="secondary" indicatorColor="secondary" sx={{ mb: 2 }}>
                        <Tab label={`Treffer (${fmtCount(hits.length)})`} sx={{ textTransform: 'none' }} />
                        <Tab label={`Eigene Bewertungen (${fmtCount(ratings.length)})`} sx={{ textTransform: 'none' }} />
                    </Tabs>

                    {tab === 0 && (
                        <>
                            <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 2, mb: 1 }}>
                                <FilterBar options={RATING_FILTERS} value={ratingFilter} onChange={setRatingFilter} label="Filter nach eigener Bewertung" />
                                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                                    {fmtCount(visibleHits.length)} von {fmtCount(hits.length)}
                                    {hiddenRejected > 0 ? `, ${fmtCount(hiddenRejected)} verworfene ausgeblendet` : ''}
                                </Typography>
                            </Stack>
                            {hits.length === 0 ? (
                                <Alert severity="info">Dieser Lauf hat keine Treffer.</Alert>
                            ) : (
                                <DataGrid
                                    rows={visibleHits}
                                    columns={columns}
                                    getRowClassName={({ row }) => (row.dossier_fazit === 'genauer_pruefen' ? 'fazit-genauer-pruefen' : '')}
                                    getRowId={(row) => row.identifier}
                                    autoHeight
                                    rowHeight={56}
                                    headerHeight={56}
                                    rowsPerPageOptions={[25, 50, 100]}
                                    initialState={{
                                        sorting: { sortModel: [{ field: 'distance_to_history', sort: 'desc' }] },
                                        pagination: { pageSize: 50 },
                                    }}
                                    columnVisibilityModel={hitVisibility}
                                    disableSelectionOnClick
                                    onRowClick={({ row }) => setSelected(row.identifier)}
                                    localeText={{ ...GRID_LOCALE, noRowsLabel: 'Keine Treffer mit diesem Filter.' }}
                                    sx={gridSx}
                                />
                            )}
                            <DataCheckSection rows={dataCheck} onOpen={setSelected} />
                        </>
                    )}

                    {tab === 1 && (
                        <>
                            <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 2, mb: 1 }}>
                                <FilterBar options={REVIEW_FILTERS} value={reviewFilter} onChange={setReviewFilter} label="Filter der eigenen Bewertungen" />
                            </Stack>
                            {ratingsError && <Alert severity="error" sx={{ mb: 1 }}>Bewertungen konnten nicht geladen werden: {ratingsError}</Alert>}
                            {!ratingsError && ratings.length === 0 && (
                                <Alert severity="info">Noch keine eigene Bewertung. In der Detailansicht eines Treffers lässt sich eine setzen.</Alert>
                            )}
                            {ratings.length > 0 && (
                                <>
                                    <DataGrid
                                        rows={visibleRatings}
                                        columns={ratingColumnsDef}
                                        getRowId={(row) => row.identifier}
                                        autoHeight
                                        rowHeight={56}
                                        headerHeight={56}
                                        rowsPerPageOptions={[25, 50, 100]}
                                        initialState={{
                                            sorting: { sortModel: [{ field: 'rated_at', sort: 'desc' }] },
                                            pagination: { pageSize: 50 },
                                        }}
                                        columnVisibilityModel={reviewVisibility}
                                        disableSelectionOnClick
                                        onRowClick={({ row }) => setSelected(row.identifier)}
                                        localeText={{ ...GRID_LOCALE, noRowsLabel: 'Keine Bewertungen mit diesem Filter.' }}
                                        sx={gridSx}
                                    />
                                    <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', mt: 0.75 }}>
                                        Kurs um Splits bereinigt, ohne Dividenden. Der Verlauf steht in der Detailansicht und sagt nichts über die Auswahl als Ganzes.
                                    </Typography>
                                </>
                            )}
                        </>
                    )}
                </>
            )}

            <OpportunityDetail
                identifier={selected}
                onClose={() => setSelected(null)}
                onRated={refreshAfterRating}
                disclaimer={disclaimer}
                dossierBudget={toNumber(run?.dossier_budget)}
            />
        </Box>
    );
};

export default Opportunities;
